import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "motion/react";
import { Check, FlaskConical, BookOpen, Clock, AlertTriangle, ShieldAlert, ShieldCheck, Activity, Ban, HelpCircle, Filter } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, faqPageSchema, localizedHead, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";
import { HeroImage } from "@/components/hero-image";
import { comparisonBarsImage } from "@/lib/responsive-images";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

interface Row {
  drug: string;
  brand: string;
  company: string;
  mechanism: string;
  loss: string;
  approval: string;
  status: "approved" | "study";
}

const rowsBase: Row[] = [
  {
    drug: "Semaglutid",
    brand: "Ozempic · Wegovy",
    company: "Novo Nordisk",
    mechanism: "GLP-1 (Mono)",
    loss: "~15 %",
    approval: "FDA 2021 · EMA 2022",
    status: "approved",
  },
  {
    drug: "Tirzepatid",
    brand: "Mounjaro · Zepbound",
    company: "Eli Lilly",
    mechanism: "GLP-1 + GIP (Dual)",
    loss: "~20–22 %",
    approval: "FDA 2023 · EMA 2023",
    status: "approved",
  },
  {
    drug: "Retatrutid",
    brand: "LY3437943",
    company: "Eli Lilly",
    mechanism: "GLP-1 + GIP + Glucagon (Triple)",
    loss: "~24 %",
    approval: "Phase III · Klinischer Studienzugang",
    status: "study",
  },
];

export const Route = createFileRoute("/vergleich")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const base = localizedHead("/vergleich", loaderData?.lang);
    const lang = loaderData?.lang ?? "en";
    const de = lang === "de";

    const drugs = [
      {
        name: "Semaglutid",
        brand: "Wegovy / Ozempic",
        code: "Semaglutide",
        manufacturer: "Novo Nordisk",
        mechanism: "GLP-1 receptor agonist",
        weightLoss: 15,
        status: "Approved",
        approval: "FDA 2021, EMA 2022",
        url: "https://en.wikipedia.org/wiki/Semaglutide",
      },
      {
        name: "Tirzepatid",
        brand: "Mounjaro / Zepbound",
        code: "Tirzepatide",
        manufacturer: "Eli Lilly",
        mechanism: "GLP-1 + GIP dual agonist",
        weightLoss: 21,
        status: "Approved",
        approval: "FDA 2023, EMA 2023",
        url: "https://en.wikipedia.org/wiki/Tirzepatide",
      },
      {
        name: "Retatrutid",
        brand: "LY3437943",
        code: "Retatrutide",
        manufacturer: "Eli Lilly",
        mechanism: "GLP-1 + GIP + glucagon triple agonist",
        weightLoss: 24,
        status: "Clinical study access",
        approval: "Phase III (TRIUMPH) – access through approved study sites",
        url: "https://en.wikipedia.org/wiki/Retatrutide",
      },
    ];

    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: de
        ? "Retatrutid vs Semaglutid vs Tirzepatid – Vergleich"
        : "Retatrutide vs Semaglutide vs Tirzepatide – comparison",
      description: de
        ? "Direkter Vergleich der drei GLP-1-basierten Adipositas-Wirkstoffe: Mechanismus, durchschnittlicher Gewichtsverlust und Zulassungsstatus."
        : "Head-to-head comparison of the three GLP-1-based obesity compounds: mechanism, average weight loss and approval status.",
      inLanguage: de ? "de-DE" : "en-US",
      numberOfItems: drugs.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: drugs.map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Drug",
          name: d.code,
          alternateName: [d.name, d.brand],
          manufacturer: { "@type": "Organization", name: d.manufacturer },
          mechanismOfAction: d.mechanism,
          clinicalPharmacology: `Average weight loss ~${d.weightLoss}%. Regulatory status: ${d.approval}.`,
          legalStatus: d.status,
          url: d.url,
        },
      })),
    };

    const faqs = de
      ? [
          { q: "Wie unterscheidet sich Retatrutid von Semaglutid?", a: "Semaglutid ist ein Mono-Agonist am GLP-1-Rezeptor. Retatrutid aktiviert zusätzlich GIP und Glucagon – und erreicht in Phase-II-Studien einen durchschnittlichen Gewichtsverlust von rund 24 % gegenüber ~ 15 % bei Semaglutid." },
          { q: "Wie unterscheidet sich Retatrutid von Tirzepatid?", a: "Tirzepatid ist ein dualer GLP-1/GIP-Agonist. Retatrutid ergänzt einen Glucagon-Rezeptor-Anteil und steigert dadurch zusätzlich den Grundumsatz. In Phase-II-Daten liegt der Gewichtsverlust bei ~ 24 % vs ~ 20–22 % bei Tirzepatid (SURMOUNT-1)." },
          { q: "Ist Retatrutid bereits zugelassen?", a: "Noch nicht als reguläres Fertigarzneimittel. Zugang ist ausschließlich über genehmigte klinische Studienprogramme möglich." },
          { q: "Welcher Wirkstoff ist heute am wirksamsten?", a: "Aktuell zugelassen ist Tirzepatid (Mounjaro/Zepbound) mit dem stärksten Effekt (~ 20–22 %). Retatrutid zeigt in Studien höhere Werte und wird über genehmigte klinische Studienprogramme bereitgestellt." },
        ]
      : [
          { q: "How does Retatrutide differ from Semaglutide?", a: "Semaglutide is a GLP-1 mono-agonist. Retatrutide additionally activates GIP and glucagon receptors — reaching an average weight loss of about 24% in Phase II versus ~ 15% for Semaglutide." },
          { q: "How does Retatrutide differ from Tirzepatide?", a: "Tirzepatide is a dual GLP-1/GIP agonist. Retatrutide adds a glucagon receptor component that additionally raises energy expenditure. In Phase II data weight loss is ~ 24% vs ~ 20–22% for Tirzepatide (SURMOUNT-1)." },
          { q: "Is Retatrutide already approved?", a: "Not yet as a regular finished pharmaceutical. Access is available exclusively through approved clinical study programs." },
          { q: "Which compound is the most effective today?", a: "The currently approved compound with the strongest effect is Tirzepatide (Mounjaro/Zepbound) at ~ 20–22%. Retatrutide shows higher values in trials and is provided through approved clinical study programs." },
        ];

    const faqPage = faqPageSchema("/vergleich", loaderData?.lang, faqs, { variant: "compare" });

    const safetyFaqs = de
      ? [
          { q: "Welche häufigen Nebenwirkungen haben GLP-1-Wirkstoffe wie Wegovy, Mounjaro und Retatrutid?", a: "Am häufigsten sind gastrointestinale Beschwerden – Übelkeit, Erbrechen, Obstipation oder Diarrhö – vor allem während der Dosis-Titration. Diese Effekte sind meist dosisabhängig und lassen mit der Zeit nach." },
          { q: "Wie hoch ist das Risiko für Pankreatitis unter GLP-1-Therapie?", a: "Akute Pankreatitis ist eine seltene, aber ernst zu nehmende Nebenwirkung aller GLP-1-basierten Wirkstoffe. Anhaltende starke Oberbauchschmerzen – insbesondere mit Ausstrahlung in den Rücken – erfordern eine sofortige ärztliche Abklärung und ein Absetzen des Wirkstoffs." },
          { q: "Gibt es ein erhöhtes Risiko für Gallenblasen-Ereignisse?", a: "Ja. Für Semaglutid und Tirzepatid ist ein erhöhtes Risiko für Cholelithiasis und Cholezystitis dokumentiert, vor allem bei rascher Gewichtsabnahme. Neue rechtsseitige Oberbauchschmerzen sollten sonografisch abgeklärt werden." },
          { q: "Wann sind GLP-1-Wirkstoffe kontraindiziert?", a: "Bei persönlicher oder familiärer Anamnese eines medullären Schilddrüsenkarzinoms (MTC) oder MEN 2, in Schwangerschaft und Stillzeit sowie bei schwerer Gastroparese oder aktiver schwerer gastrointestinaler Erkrankung." },
          { q: "Welche zusätzlichen Sicherheitssignale gibt es für Retatrutid?", a: "In Phase-II-Daten zu Retatrutid wurden ein moderater Herzfrequenz-Anstieg von ~ 5–7 bpm sowie leichte Lipid-Verschiebungen beobachtet. Langzeit-Sicherheit über 2 Jahre sowie kardiovaskuläre Endpunkte sind bislang nicht validiert." },
          { q: "Welches Monitoring ist unter GLP-1-Therapie sinnvoll?", a: "Regelmäßige Kontrolle von Gewicht, Blutdruck, HbA1c, Lipiden und Nierenfunktion. Bei Diabetes sollten Insulin- oder Sulfonylharnstoff-Dosen angepasst werden (Hypoglykämie-Risiko). Auf ausreichende Flüssigkeitszufuhr achten, besonders bei Übelkeit oder Erbrechen." },
          { q: "Ersetzen diese Informationen eine ärztliche Beratung?", a: "Nein. Diese Seite dient nur der Information. Verordnung, Titration, Verträglichkeitsprüfung und Monitoring gehören in ärztliche Hand. Bei Nebenwirkungen oder Unsicherheiten immer den behandelnden Arzt oder die Ärztin kontaktieren." },
        ]
      : [
          { q: "What are the common side effects of GLP-1 drugs like Wegovy, Mounjaro and Retatrutide?", a: "The most common effects are gastrointestinal — nausea, vomiting, constipation or diarrhea — mostly during dose titration. They are usually dose-dependent and ease over time." },
          { q: "How high is the pancreatitis risk with GLP-1 therapy?", a: "Acute pancreatitis is a rare but serious adverse event across GLP-1-based compounds. Persistent severe upper-abdominal pain — especially radiating to the back — requires immediate medical work-up and discontinuation of the drug." },
          { q: "Is there an increased risk of gallbladder events?", a: "Yes. Both Semaglutide and Tirzepatide show an increased rate of cholelithiasis and cholecystitis, particularly during rapid weight loss. New right-sided upper-abdominal pain should be evaluated with ultrasound." },
          { q: "When are GLP-1 drugs contraindicated?", a: "In personal or family history of medullary thyroid carcinoma (MTC) or MEN 2, during pregnancy and breastfeeding, and in severe gastroparesis or active severe gastrointestinal disease." },
          { q: "What additional safety signals exist for Retatrutide?", a: "Phase II data for Retatrutide show a moderate heart-rate increase of ~ 5–7 bpm and mild lipid shifts. Long-term safety beyond 2 years and cardiovascular endpoints have not yet been validated." },
          { q: "What monitoring is advisable under GLP-1 therapy?", a: "Regular checks of weight, blood pressure, HbA1c, lipids and renal function. In diabetes, insulin or sulfonylurea doses should be adjusted (hypoglycemia risk). Ensure adequate hydration, especially during nausea or vomiting." },
          { q: "Does this page replace medical advice?", a: "No. This page is informational only. Prescription, titration, tolerability assessment and monitoring belong in a clinician's hands. Contact your treating physician for any side effects or uncertainty." },
        ];

    const safetyFaqPage = faqPageSchema("/vergleich", loaderData?.lang, safetyFaqs, {
      variant: "safety",
      name: de
        ? "Sicherheit und häufige Nebenwirkungen – Retatrutid, Wegovy, Mounjaro"
        : "Safety and common side effects — Retatrutide, Wegovy, Mounjaro",
      about: {
        "@type": "MedicalCondition",
        name: de ? "GLP-1-basierte Adipositas-Therapie" : "GLP-1 based obesity pharmacotherapy",
      },
    });


    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/vergleich", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        { type: "application/ld+json", children: JSON.stringify(faqPage) },
        { type: "application/ld+json", children: JSON.stringify(safetyFaqPage) },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { lang } = useI18n();
  const de = lang === "de";
  const c = de
    ? {
        eyebrow: "Head-to-Head",
        h1: "Retatrutid vs Semaglutid vs Tirzepatid",
        lead: "Die drei relevanten GLP-1-basierten Adipositas-Wirkstoffe im direkten Vergleich – Mechanismus, Effektstärke und Zulassungsstatus 2026.",
        th: {
          drug: "Wirkstoff",
          brand: "Markennamen",
          company: "Hersteller",
          mech: "Mechanismus",
          loss: "Ø Gewichtsverlust",
          appr: "Zulassung",
        },
        approvedLabel: "Zugelassen",
        pendingLabel: "Klinische Studie",
        keyTitle: "Der entscheidende Unterschied",
        keyBody:
          "Semaglutid adressiert nur GLP-1 (Sättigung). Tirzepatid ergänzt GIP (Insulin-Sensitivität). Retatrutid setzt zusätzlich am Glucagon-Rezeptor an – und erhöht damit als einziger dieser Wirkstoffe direkt den Grundumsatz.",
        picks: [
          { t: "Wegovy / Ozempic", b: "Etablierte Erstlinien-Option, breite Verfügbarkeit, umfangreiche Langzeit-Daten." },
          { t: "Mounjaro / Zepbound", b: "Aktuell stärkster zugelassener Wirkstoff, dual-agonistisch, starke Wirkung auf Blutzucker." },
          { t: "Retatrutid (LY3437943)", b: "Höchste Wirksamkeit in Phase-II/III-Studien; Zugang ausschließlich über genehmigte klinische Studienprogramme." },
        ],
      }
    : {
        eyebrow: "Head-to-Head",
        h1: "Retatrutide vs Semaglutide vs Tirzepatide",
        lead: "The three relevant GLP-1-based obesity compounds compared directly — mechanism, effect size and 2026 regulatory status.",
        th: {
          drug: "Compound",
          brand: "Brands",
          company: "Manufacturer",
          mech: "Mechanism",
          loss: "Avg weight loss",
          appr: "Approval",
        },
        approvedLabel: "Approved",
        pendingLabel: "Clinical study",
        keyTitle: "The decisive difference",
        keyBody:
          "Semaglutide only targets GLP-1 (satiety). Tirzepatide adds GIP (insulin sensitivity). Retatrutide additionally engages the glucagon receptor — the only one of the three that directly raises basal metabolic rate.",
        picks: [
          { t: "Wegovy / Ozempic", b: "Established first-line option, broad availability, extensive long-term data." },
          { t: "Mounjaro / Zepbound", b: "Currently the most effective approved compound, dual-agonist, strong glycemic effect." },
          { t: "Retatrutide (LY3437943)", b: "Highest efficacy in Phase II/III data; access exclusively through approved clinical study programs." },
        ],
      };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {c.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{c.lead}</p>
      </div>

      <HeroImage
        {...comparisonBarsImage}
        alt="Editorial data visualization comparing GLP-1 therapy generations"
        eager
      />


      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-10 overflow-hidden rounded-2xl border border-border bg-card"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">{c.th.drug}</th>
                <th className="px-4 py-3 font-medium">{c.th.brand}</th>
                <th className="px-4 py-3 font-medium">{c.th.company}</th>
                <th className="px-4 py-3 font-medium">{c.th.mech}</th>
                <th className="px-4 py-3 font-medium">{c.th.loss}</th>
                <th className="px-4 py-3 font-medium">{c.th.appr}</th>
              </tr>
            </thead>
            <tbody>
              {rowsBase.map((r, i) => (
                <tr
                  key={r.drug}
                  className={`border-t border-border ${i === 2 ? "bg-emerald/5" : ""}`}
                >
                  <td className="px-4 py-4 font-semibold text-foreground">{r.drug}</td>
                  <td className="px-4 py-4 text-muted-foreground">{r.brand}</td>
                  <td className="px-4 py-4 text-muted-foreground">{r.company}</td>
                  <td className="px-4 py-4 text-foreground">{r.mechanism}</td>
                  <td className="px-4 py-4 font-mono font-semibold text-foreground">{r.loss}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      {r.status === "approved" ? (
                        <Check className="h-4 w-4 text-emerald" />
                      ) : (
                        <FlaskConical className="h-4 w-4 text-indigo" />
                      )}
                      <span className="text-xs text-muted-foreground">{r.approval}</span>
                    </div>
                    <span className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {r.status === "approved" ? c.approvedLabel : c.pendingLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <section className="mt-16 rounded-2xl border border-border bg-card p-6 md:p-10">
        <h2 className="font-display text-2xl text-foreground md:text-3xl">{c.keyTitle}</h2>
        <p className="mt-3 text-muted-foreground">{c.keyBody}</p>
      </section>

      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {c.picks.map((p) => (
          <div key={p.t} className="card-elevated p-5">
            <h3 className="text-base font-semibold text-foreground">{p.t}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{p.b}</p>
          </div>
        ))}
      </div>

      <DeepDives de={de} />

      <ConsultCta variant="section" />
      <RelatedLinks only={["/wissenschaft", "/zulassung", "/thailand"]} />
    </div>
  );
}

function ExplainedBox({
  label,
  items,
}: {
  label: string;
  items: { bullet: string; help: string }[];
}) {
  return (
    <aside
      aria-label={label}
      className="mt-5 rounded-xl border-l-4 border-primary bg-primary/5 p-5"
    >
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground">
          <HelpCircle className="h-3.5 w-3.5" />
        </span>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
          {label}
        </p>
      </div>

      <TooltipProvider delayDuration={100}>
        <ul className="mt-4 space-y-2">
          {items.map((item) => (
            <li key={item.bullet} className="flex gap-2 text-xs text-muted-foreground">
              <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-primary" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className="cursor-help underline decoration-dashed decoration-primary underline-offset-2 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {item.bullet}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-[16rem]">
                  <p>{item.help}</p>
                </TooltipContent>
              </Tooltip>
            </li>
          ))}
        </ul>
      </TooltipProvider>
    </aside>
  );
}

function DeepDives({ de }: { de: boolean }) {
  const wegovy = de
    ? {
        h: "Wegovy vs Retatrutid",
        lead: "Wegovy (Semaglutid 2,4 mg wöchentlich, Novo Nordisk) ist die etablierte Erstlinien-Option zur medikamentösen Adipositas-Therapie. Retatrutid ist der potenzielle Nachfolger.",
        rows: [
          ["Mechanismus", "GLP-1-Mono-Agonist", "GLP-1 + GIP + Glucagon"],
          ["Ø Gewichtsverlust", "~ 15 % (STEP-1, 68 Wochen)", "~ 24 % (Phase II, 48 Wochen)"],
          ["Dosis", "Steigerung bis 2,4 mg / Woche s.c.", "Steigerung bis 12 mg / Woche s.c."],
          ["Zulassung", "FDA 2021, EMA 2022", "Voraussichtlich 2026 / 27"],
          ["Verfügbarkeit", "Rezeptpflichtig, weltweit verfügbar", "Nur innerhalb klinischer Studien"],
        ],
        take: "Wer heute mit einer GLP-1-Therapie beginnen möchte, ist mit Wegovy (oder Mounjaro) versorgt. Retatrutid ist erst 2026/27 eine reale Option.",
        evidence: {
          studies: [
            { name: "STEP-1", ref: "Wilding et al., NEJM 2021", detail: "1.961 Erwachsene, Semaglutid 2,4 mg vs Placebo, 68 Wochen.", phases: ["phase3", "longterm"] as const },
            { name: "Retatrutid Phase II", ref: "Jastreboff et al., NEJM 2023", detail: "338 Erwachsene, Retatrutid 1–12 mg vs Placebo, 48 Wochen.", phases: ["phase2"] as const },
          ],
          timeframe: "STEP-1: 68 Wochen · Reta Phase II: 48 Wochen – ein direkter Head-to-Head über die gleiche Dauer existiert bislang nicht.",
          uncertainties: [
            "Kein randomisierter direkter Vergleich (RCT) zwischen Wegovy und Retatrutid publiziert.",
            "Retatrutid-Daten stammen aus Phase II mit deutlich kleinerer Kohorte – Phase-III-Effektstärken können abweichen.",
            "Langzeit-Sicherheit (> 2 Jahre) und kardiovaskuläre Endpunkte für Retatrutid noch offen.",
          ],
          explained: [
            { bullet: "Indirekter Vergleich", help: "Die Gewichtsverluste stammen aus zwei getrennten Studien, nicht aus einem direkten Head-to-Head-Versuch." },
            { bullet: "Phase-II-Präparat", help: "Retatrutid ist noch in der Entwicklung. Die Phase-III-Daten können die Wirkung bestätigen oder verändern." },
            { bullet: "Langzeit-Follow-up fehlt", help: "Wegovy wurde über mehrere Jahre beobachtet; Retatrutid hat noch keine Langzeit-Erfahrung außerhalb klinischer Studien." },
            { bullet: "Nicht jeder Patient ist gleich", help: "Studienteilnehmer waren meist adipös mit Begleiterkrankungen; die persönliche Wirkung kann davon abweichen." },
          ],
        },
        safety: {
          shared: [
            "Häufig: Übelkeit, Erbrechen, Obstipation oder Diarrhö – vor allem in der Titrationsphase.",
            "Gallenblasen-Ereignisse (Cholelithiasis, Cholezystitis) sind bei GLP-1-Wirkstoffen erhöht beschrieben.",
            "Selten: akute Pankreatitis – bei anhaltenden Oberbauchschmerzen sofort abklären.",
          ],
          contraindications: [
            "Persönliche oder familiäre Anamnese eines medullären Schilddrüsenkarzinoms (MTC) oder MEN 2.",
            "Schwangerschaft und Stillzeit (mind. 2 Monate vor geplanter Konzeption absetzen).",
            "Schwere Gastroparese oder aktive schwere gastrointestinale Erkrankung.",
          ],
          monitoring: [
            "Regelmäßig Gewicht, Blutdruck, HbA1c und Nierenfunktion kontrollieren.",
            "Bei Diabetes: Insulin- oder Sulfonylharnstoff-Dosis anpassen (Hypoglykämie-Risiko).",
            "Auf Dehydratation achten – bei starker Übelkeit / Erbrechen Flüssigkeitszufuhr sichern.",
          ],
          reta: "Retatrutid: zusätzliche unklare Langzeit-Signale zu Herzfrequenz-Anstieg (~ 5–7 bpm in Phase II) und moderaten Lipid-Verschiebungen – im Alltag außerhalb kontrollierter Studien nicht validiert.",
          disclaimer: "Diese Seite ersetzt keine ärztliche Beratung. Verordnung, Titration und Monitoring gehören in ärztliche Hand.",
        },
      }
    : {
        h: "Wegovy vs Retatrutide",
        lead: "Wegovy (Semaglutide 2.4 mg weekly, Novo Nordisk) is the established first-line pharmacotherapy for obesity. Retatrutide is the potential successor.",
        rows: [
          ["Mechanism", "GLP-1 mono-agonist", "GLP-1 + GIP + glucagon"],
          ["Avg weight loss", "~ 15% (STEP-1, 68 weeks)", "~ 24% (Phase II, 48 weeks)"],
          ["Dosing", "Titration to 2.4 mg / week s.c.", "Titration to 12 mg / week s.c."],
          ["Approval", "FDA 2021, EMA 2022", "Expected 2026 / 27"],
          ["Availability", "Prescription, worldwide", "Only within clinical trials"],
        ],
        take: "Anyone starting GLP-1 therapy today is covered by Wegovy (or Mounjaro). Retatrutide only becomes a real option in 2026/27.",
        evidence: {
          studies: [
            { name: "STEP-1", ref: "Wilding et al., NEJM 2021", detail: "1,961 adults, Semaglutide 2.4 mg vs placebo, 68 weeks.", phases: ["phase3", "longterm"] as const },
            { name: "Retatrutide Phase II", ref: "Jastreboff et al., NEJM 2023", detail: "338 adults, Retatrutide 1–12 mg vs placebo, 48 weeks.", phases: ["phase2"] as const },
          ],
          timeframe: "STEP-1: 68 weeks · Reta Phase II: 48 weeks — no direct head-to-head over the same duration has been published.",
          uncertainties: [
            "No randomized head-to-head trial (RCT) between Wegovy and Retatrutide published.",
            "Retatrutide data come from Phase II with a much smaller cohort — Phase III effect sizes may differ.",
            "Long-term safety (> 2 years) and cardiovascular endpoints for Retatrutide are still open.",
          ],
          explained: [
            { bullet: "Indirect comparison", help: "Weight-loss figures come from two separate trials, not a direct head-to-head study." },
            { bullet: "Phase II compound", help: "Retatrutide is still in development. Phase III data may confirm or change the observed effect." },
            { bullet: "Long-term follow-up missing", help: "Wegovy has been observed for several years; Retatrutide has no long-term experience outside clinical trials yet." },
            { bullet: "Not every patient is the same", help: "Trial participants were mostly obese with comorbidities; your personal response may differ." },
          ],
        },
        safety: {
          shared: [
            "Common: nausea, vomiting, constipation or diarrhea — mostly during titration.",
            "Gallbladder events (cholelithiasis, cholecystitis) are reported at increased rates with GLP-1 drugs.",
            "Rare: acute pancreatitis — persistent upper-abdominal pain requires immediate work-up.",
          ],
          contraindications: [
            "Personal or family history of medullary thyroid carcinoma (MTC) or MEN 2.",
            "Pregnancy and breastfeeding (discontinue at least 2 months before planned conception).",
            "Severe gastroparesis or active severe gastrointestinal disease.",
          ],
          monitoring: [
            "Track weight, blood pressure, HbA1c and renal function regularly.",
            "In diabetes: adjust insulin or sulfonylurea dosing (hypoglycemia risk).",
            "Watch for dehydration — secure fluid intake during heavy nausea / vomiting.",
          ],
          reta: "Retatrutide: additional open long-term signals on heart-rate increase (~ 5–7 bpm in Phase II) and modest lipid shifts — not validated outside controlled trials.",
          disclaimer: "This page does not replace medical advice. Prescription, titration and monitoring belong in a clinician's hands.",
        },
      };

  const mounjaro = de
    ? {
        h: "Mounjaro vs Retatrutid",
        lead: "Mounjaro (Tirzepatid, Eli Lilly) ist derzeit der stärkste zugelassene Wirkstoff. Retatrutid stammt vom selben Hersteller und erweitert das Prinzip um einen dritten Rezeptor.",
        rows: [
          ["Mechanismus", "GLP-1 + GIP (dual)", "GLP-1 + GIP + Glucagon (triple)"],
          ["Ø Gewichtsverlust", "~ 20–22 % (SURMOUNT-1, 72 Wochen)", "~ 24 % (Phase II, 48 Wochen)"],
          ["Zusatzeffekt", "Insulin-Sensitivität ↑", "Zusätzlich Grundumsatz ↑ über Glucagon"],
          ["Zulassung", "FDA 2023, EMA 2023 (Zepbound: FDA 2023)", "Voraussichtlich 2026 / 27"],
          ["Verfügbarkeit", "Rezeptpflichtig, weltweit anlaufend", "Nur innerhalb klinischer Studien"],
        ],
        take: "Der Sprung von Mounjaro auf Retatrutid ist deutlich kleiner als der Sprung von Wegovy auf Mounjaro – der zusätzliche Nutzen liegt vor allem im aktiven Energieverbrauch.",
        evidence: {
          studies: [
            { name: "SURMOUNT-1", ref: "Jastreboff et al., NEJM 2022", detail: "2.539 Erwachsene, Tirzepatid 5/10/15 mg vs Placebo, 72 Wochen.", phases: ["phase3", "longterm"] as const },
            { name: "Retatrutid Phase II", ref: "Jastreboff et al., NEJM 2023", detail: "338 Erwachsene, Retatrutid 1–12 mg vs Placebo, 48 Wochen.", phases: ["phase2"] as const },
            { name: "TRIUMPH-1 (laufend)", ref: "NCT05929066, Eli Lilly", detail: "~ 2.100 Erwachsene mit Adipositas, 80 Wochen, Placebo-kontrolliert – Ergebnisse für 2026 erwartet.", phases: ["phase3"] as const },
          ],
          timeframe: "SURMOUNT-1: 72 Wochen · Reta Phase II: 48 Wochen · TRIUMPH-1: 80 Wochen (Endpunkt 2026).",
          uncertainties: [
            "Der Vorsprung von ~ 2 – 4 Prozentpunkten könnte in Phase III schrumpfen oder wachsen.",
            "Höhere Wirksamkeit geht mit stärkerer Titrations-Phase einher – GI-Verträglichkeit im Alltag noch nicht validiert.",
            "Effekt der Glucagon-Achse auf Leber, Herzfrequenz und Lipide braucht Langzeit-Follow-up.",
          ],
          explained: [
            { bullet: "Gleicher Hersteller, andere Rezeptoren", help: "Mounjaro und Retatrutid stammen beide von Eli Lilly, wirken aber an unterschiedlichen Rezeptoren." },
            { bullet: "Geringerer Abstand", help: "Der Unterschied im Gewichtsverlust ist kleiner als bei Wegovy. Ob er klinisch relevant ist, zeigt erst Phase III." },
            { bullet: "Glucagon-Achse", help: "Der zusätzliche Glucagon-Effekt kann den Energieverbrauch steigern, ist aber weniger erforscht." },
            { bullet: "Alltagstoleranz", help: "In Studien werden Nebenwirkungen gezielt erfasst; im Alltag können sie anders verlaufen." },
          ],
        },
        safety: {
          shared: [
            "Häufig: Übelkeit, Diarrhö, Obstipation – meist während der Titration; oft dosisabhängig.",
            "Gallenblasen-Ereignisse und selten Pankreatitis auch für Tirzepatid berichtet.",
            "Injektionsstellen-Reaktionen und leichte Herzfrequenz-Erhöhung (~ 2–5 bpm) möglich.",
          ],
          contraindications: [
            "Persönliche oder familiäre MTC-/MEN-2-Anamnese.",
            "Schwangerschaft und Stillzeit; kein Einsatz bei Typ-1-Diabetes.",
            "Schwere Gastroparese oder aktive schwere gastrointestinale Erkrankung.",
          ],
          monitoring: [
            "HbA1c, Nüchternglukose, Lipide und Nierenfunktion im Verlauf kontrollieren.",
            "Insulin- / Sulfonylharnstoff-Dosis anpassen (Hypoglykämie-Risiko).",
            "Bei diabetischer Retinopathie: augenärztliche Kontrolle vor rascher Blutzucker-Senkung.",
          ],
          reta: "Retatrutid: zusätzliche Glucagon-Achse – noch offene Signale für Herzfrequenz-Anstieg, hepatische Effekte und mögliche Blutzucker-Ambivalenz bei sehr hohen Dosen; nur unter Studienbedingungen dokumentiert.",
          disclaimer: "Diese Seite ersetzt keine ärztliche Beratung. Verordnung, Titration und Monitoring gehören in ärztliche Hand.",
        },
      }
    : {
        h: "Mounjaro vs Retatrutide",
        lead: "Mounjaro (Tirzepatide, Eli Lilly) is currently the most effective approved compound. Retatrutide comes from the same manufacturer and extends the principle by a third receptor.",
        rows: [
          ["Mechanism", "GLP-1 + GIP (dual)", "GLP-1 + GIP + glucagon (triple)"],
          ["Avg weight loss", "~ 20–22% (SURMOUNT-1, 72 weeks)", "~ 24% (Phase II, 48 weeks)"],
          ["Additional effect", "Insulin sensitivity ↑", "Additionally: energy expenditure ↑ via glucagon"],
          ["Approval", "FDA 2023, EMA 2023 (Zepbound: FDA 2023)", "Expected 2026 / 27"],
          ["Availability", "Prescription, rolling out worldwide", "Only within clinical trials"],
        ],
        take: "The step from Mounjaro to Retatrutide is much smaller than the step from Wegovy to Mounjaro — the additional benefit lies mainly in active energy expenditure.",
        evidence: {
          studies: [
            { name: "SURMOUNT-1", ref: "Jastreboff et al., NEJM 2022", detail: "2,539 adults, Tirzepatide 5/10/15 mg vs placebo, 72 weeks.", phases: ["phase3", "longterm"] as const },
            { name: "Retatrutide Phase II", ref: "Jastreboff et al., NEJM 2023", detail: "338 adults, Retatrutide 1–12 mg vs placebo, 48 weeks.", phases: ["phase2"] as const },
            { name: "TRIUMPH-1 (ongoing)", ref: "NCT05929066, Eli Lilly", detail: "~ 2,100 adults with obesity, 80 weeks, placebo-controlled — results expected in 2026.", phases: ["phase3"] as const },
          ],
          timeframe: "SURMOUNT-1: 72 weeks · Reta Phase II: 48 weeks · TRIUMPH-1: 80 weeks (readout 2026).",
          uncertainties: [
            "The ~ 2 – 4 percentage-point lead could shrink or grow in Phase III.",
            "Higher efficacy comes with a stronger titration phase — real-world GI tolerability not yet validated.",
            "Effects of the glucagon axis on liver, heart rate and lipids need long-term follow-up.",
          ],
          explained: [
            { bullet: "Same manufacturer, different receptors", help: "Mounjaro and Retatrutide both come from Eli Lilly, but act on different receptors." },
            { bullet: "Smaller gap", help: "The weight-loss difference is smaller than with Wegovy. Whether it is clinically meaningful awaits Phase III." },
            { bullet: "Glucagon axis", help: "The additional glucagon effect may raise energy expenditure, but is less studied." },
            { bullet: "Real-world tolerability", help: "Trials capture side effects systematically; everyday experience may differ." },
          ],
        },
        safety: {
          shared: [
            "Common: nausea, diarrhea, constipation — mostly during titration and often dose-dependent.",
            "Gallbladder events and rare pancreatitis also reported for Tirzepatide.",
            "Injection-site reactions and mild heart-rate increase (~ 2–5 bpm) possible.",
          ],
          contraindications: [
            "Personal or family history of MTC / MEN 2.",
            "Pregnancy and breastfeeding; not for type 1 diabetes.",
            "Severe gastroparesis or active severe gastrointestinal disease.",
          ],
          monitoring: [
            "Follow HbA1c, fasting glucose, lipids and renal function over time.",
            "Adjust insulin / sulfonylurea dosing (hypoglycemia risk).",
            "With diabetic retinopathy: ophthalmologic check before rapid glucose lowering.",
          ],
          reta: "Retatrutide: extra glucagon axis — open signals on heart-rate increase, hepatic effects and potential glycemic ambivalence at very high doses; documented only under trial conditions.",
          disclaimer: "This page does not replace medical advice. Prescription, titration and monitoring belong in a clinician's hands.",
        },
      };

  const th = de ? ["Kriterium", "Alternative", "Retatrutid"] : ["Criterion", "Alternative", "Retatrutide"];
  const L = de
    ? { evidence: "Evidenz", studies: "Studienbezug", time: "Zeitrahmen", risk: "Unsicherheiten", explained: "Unsicherheiten erklärt", safety: "Sicherheit", common: "Häufige Nebenwirkungen", contra: "Kontraindikationen", monitor: "Monitoring", retaNote: "Retatrutid-spezifisch", disclaimer: "Hinweis", banner: "Diese Sektionen sind bewusst defensiv: nur belegte Effekte, klare Grenzen, keine Off-Label-Empfehlung.", filter: "Zeitrahmen filtern", empty: "Keine Studien in dieser Kategorie – wähle einen anderen Zeitrahmen." }
    : { evidence: "Evidence", studies: "Study reference", time: "Timeframe", risk: "Uncertainties", explained: "Uncertainties explained", safety: "Safety", common: "Common side effects", contra: "Contraindications", monitor: "Monitoring", retaNote: "Retatrutide-specific", disclaimer: "Note", banner: "These sections are deliberately defensive: only documented effects, clear limits, no off-label recommendation.", filter: "Filter timeframe", empty: "No studies in this category — pick a different timeframe." };

  type Phase = "all" | "phase2" | "phase3" | "longterm";
  const [phase, setPhase] = useState<Phase>("all");
  const phaseOptions: { key: Phase; label: string }[] = [
    { key: "all", label: de ? "Alle" : "All" },
    { key: "phase2", label: "Phase II" },
    { key: "phase3", label: "Phase III" },
    { key: "longterm", label: de ? "Langzeit (≥ 68 Wochen)" : "Long-term (≥ 68 weeks)" },
  ];

  return (
    <>
      <aside
        role="note"
        aria-label={de ? "Sicherheitshinweis zu den Vergleichs-Sektionen" : "Safety notice for the comparison sections"}
        className="mt-16 flex items-start gap-3 rounded-xl border-l-4 border-amber-500 bg-amber-500/10 p-4 text-sm text-foreground"
      >
        <ShieldAlert aria-hidden="true" className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400" />
        <p>{L.banner}</p>
      </aside>

      <div
        role="group"
        aria-label={L.filter}
        className="mt-6 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card/60 p-3"
      >
        <span aria-hidden="true" className="flex items-center gap-1.5 pl-1 pr-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> {L.filter}
        </span>
        {phaseOptions.map((opt) => {
          const active = phase === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setPhase(opt.key)}
              aria-pressed={active}
              aria-label={`${L.filter}: ${opt.label}`}
              className={
                "rounded-full border px-3 py-1 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
                (active
                  ? "border-indigo bg-indigo text-indigo-foreground"
                  : "border-border bg-background text-foreground hover:border-indigo/60 hover:text-indigo")
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {[wegovy, mounjaro].map((s, si) => (
        <section key={s.h} className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <h2 className="font-display text-2xl text-foreground md:text-3xl">{s.h}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{s.lead}</p>
          <div className="mt-5 overflow-hidden rounded-xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {th.map((h) => (
                    <th key={h} className="px-3 py-2 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.rows.map((r, i) => (
                  <tr key={r[0]} className={i > 0 ? "border-t border-border" : ""}>
                    <td className="px-3 py-2.5 font-medium text-foreground">{r[0]}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{r[1]}</td>
                    <td className="px-3 py-2.5 text-foreground">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside
            aria-labelledby={`evidence-title-${si}`}
            className="mt-6 rounded-xl border-l-4 border-indigo bg-indigo/5 p-5"
          >
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-md bg-indigo text-indigo-foreground">
                <BookOpen className="h-3.5 w-3.5" />
              </span>
              <h3 id={`evidence-title-${si}`} className="text-[11px] font-semibold uppercase tracking-widest text-indigo">
                {L.evidence}
              </h3>
            </div>

            <div className="mt-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                <BookOpen aria-hidden="true" className="h-3.5 w-3.5 text-indigo" /> {L.studies}
              </h4>
              {(() => {
                const filtered = phase === "all"
                  ? s.evidence.studies
                  : s.evidence.studies.filter((st) => (st.phases as readonly string[]).includes(phase));
                if (filtered.length === 0) {
                  return (
                    <p className="mt-2 rounded-md border border-dashed border-border/60 bg-background/40 p-3 text-xs italic text-foreground">
                      {L.empty}
                    </p>
                  );
                }
                return (
                  <ul className="mt-2 space-y-2">
                    {filtered.map((st) => (
                      <li key={st.name} className="rounded-md border border-border/60 bg-background/60 p-3 text-xs">
                        <p className="font-semibold text-foreground">
                          {st.name}
                          <span className="ml-2 font-normal text-muted-foreground">· {st.ref}</span>
                          <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                            {st.phases.map((p) => (
                              <span key={p} className="rounded-full border border-indigo/40 bg-indigo/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo">
                                {p === "phase2" ? "Phase II" : p === "phase3" ? "Phase III" : de ? "Langzeit" : "Long-term"}
                              </span>
                            ))}
                          </span>
                        </p>
                        <p className="mt-1 text-foreground/90">{st.detail}</p>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>

            <div className="mt-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                <Clock aria-hidden="true" className="h-3.5 w-3.5 text-indigo" /> {L.time}
              </h4>
              <p className="mt-1 text-xs text-foreground/90">{s.evidence.timeframe}</p>
            </div>

            <div className="mt-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" /> {L.risk}
              </h4>
              <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                {s.evidence.uncertainties.map((u) => (
                  <li key={u} className="flex gap-2">
                    <span aria-hidden="true" className="mt-1.5 h-1 w-1 flex-none rounded-full bg-amber-600 dark:bg-amber-400" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <ExplainedBox label={L.explained} items={s.evidence.explained} />

          <aside
            aria-labelledby={`safety-title-${si}`}
            className="mt-5 rounded-xl border-l-4 border-amber-500 bg-amber-500/5 p-5"
          >
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-md bg-amber-500 text-white">
                <ShieldAlert className="h-3.5 w-3.5" />
              </span>
              <h3 id={`safety-title-${si}`} className="text-[11px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-300">
                {L.safety}
              </h3>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <Activity aria-hidden="true" className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" /> {L.common}
                </h4>
                <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                  {s.safety.shared.map((x) => (
                    <li key={x} className="flex gap-2">
                      <span aria-hidden="true" className="mt-1.5 h-1 w-1 flex-none rounded-full bg-amber-600 dark:bg-amber-400" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <Ban aria-hidden="true" className="h-3.5 w-3.5 text-red-600 dark:text-red-400" /> {L.contra}
                </h4>
                <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                  {s.safety.contraindications.map((x) => (
                    <li key={x} className="flex gap-2">
                      <span aria-hidden="true" className="mt-1.5 h-1 w-1 flex-none rounded-full bg-red-600 dark:bg-red-400" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground">
                <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 text-emerald" /> {L.monitor}
              </h4>
              <ul className="mt-2 space-y-1.5 text-xs text-foreground/90">
                {s.safety.monitoring.map((x) => (
                  <li key={x} className="flex gap-2">
                    <span aria-hidden="true" className="mt-1.5 h-1 w-1 flex-none rounded-full bg-emerald" />
                    {x}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-md border border-border/60 bg-background/60 p-3 text-xs">
              <h4 className="font-semibold text-foreground">{L.retaNote}</h4>
              <p className="mt-1 text-foreground/90">{s.safety.reta}</p>
            </div>

            <p className="mt-3 text-xs italic text-foreground/80">
              <strong className="not-italic text-foreground">{L.disclaimer}: </strong>
              {s.safety.disclaimer}
            </p>
          </aside>

          <p className="mt-4 text-sm text-muted-foreground">
            <strong className="text-foreground">{de ? "Fazit: " : "Takeaway: "}</strong>
            {s.take}
          </p>
        </section>
      ))}
      </div>
    </>
  );
}
